import { Box, Chip, IconButton, Stack, Switch, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import { SquarePenIcon } from 'lucide-react';
import { useState } from 'react';
import { FeatureFlag } from '../../types/FeatureFlag/FeatureFlag.type';
import { FeatureFlagAccess } from '../../types/FeatureFlag/FeatureFlagUpdate.type';
import FeatureFlagAccessModal from './FeatureFlagAccessModal.tsx';

interface FeatureFlagRowProps {
  flag: FeatureFlag;
  isLoading: boolean;
  onToggle: (flag: FeatureFlag, enabled: boolean) => void;
  onAccessChange: (flag: FeatureFlag, access: FeatureFlagAccess) => void;
}

const countLabel = (count: number, singular: string, plural: string) => `${count} ${count === 1 ? singular : plural}`;

function FeatureFlagRow({ flag, isLoading, onToggle, onAccessChange }: FeatureFlagRowProps) {
  const [accessModalOpen, setAccessModalOpen] = useState(false);

  const audienceChips = [
    {
      label: countLabel(flag.counties.length, 'county', 'counties'),
      names: flag.counties.map((county) => county.name),
    },
    { label: countLabel(flag.teams.length, 'team', 'teams'), names: flag.teams.map((team) => team.name) },
    { label: countLabel(flag.users.length, 'user', 'users'), names: flag.users.map((user) => user.email) },
  ].filter((group) => group.names.length > 0);

  const handleAccessSave = (access: FeatureFlagAccess) => {
    onAccessChange(flag, access);
    setAccessModalOpen(false);
  };

  return (
    <TableRow hover>
      <TableCell width={340}>
        <Typography variant='body2' sx={{ fontWeight: 500 }}>
          {flag.name}
        </Typography>
        <Typography variant='caption' sx={{ color: 'text.secondary' }}>
          {flag.description || 'No description'}
        </Typography>
      </TableCell>
      <TableCell width={340}>
        {audienceChips.length > 0 ? (
          <Stack direction='row' spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            {audienceChips.map((group) => (
              <Tooltip key={group.label} title={group.names.join(', ')}>
                <Chip size='small' label={group.label} variant={flag.enabled ? 'filled' : 'outlined'} />
              </Tooltip>
            ))}
          </Stack>
        ) : (
          <Chip size='small' label={'Not set'} variant='outlined' />
        )}
      </TableCell>
      <TableCell width={200}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Switch
            checked={flag.enabled}
            disabled={isLoading}
            onChange={(event) => onToggle(flag, event.target.checked)}
            slotProps={{ input: { 'aria-label': `Toggle ${flag.name}` } }}
          />
          <Typography variant='body2' sx={{ color: flag.enabled ? 'text.primary' : 'text.secondary' }}>
            {flag.enabled ? 'Enabled' : 'Disabled'}
          </Typography>
        </Box>
      </TableCell>
      <TableCell align='right' width={120}>
        <Tooltip title='Edit visibility'>
          <span>
            <IconButton onClick={() => setAccessModalOpen(true)} disabled={isLoading} aria-label='Edit visibility'>
              <SquarePenIcon size={20} />
            </IconButton>
          </span>
        </Tooltip>
      </TableCell>
      <FeatureFlagAccessModal
        open={accessModalOpen}
        onClose={() => setAccessModalOpen(false)}
        flag={flag}
        loading={isLoading}
        onSave={handleAccessSave}
      />
    </TableRow>
  );
}

export default FeatureFlagRow;
